import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapPin, Clock, Users, Lightbulb, Heart, Target, Send } from 'lucide-react';
import { toast } from 'sonner';

const Careers = () => {
  const [applicationData, setApplicationData] = useState({
    position: '',
    fullName: '',
    location: '',
    phone: '',
    email: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setApplicationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value) => {
    setApplicationData(prev => ({
      ...prev,
      position: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast.success("Thank you for your application! We'll review it and get back to you soon.");
    setApplicationData({
      position: '',
      fullName: '',
      location: '',
      phone: '',
      email: '',
      message: ''
    });
  };

  const openRoles = [
    {
      title: "UI Designer",
      slug: "ui-designer",
      department: "Design",
      location: "Perth, Australia",
      type: "Full-time",
      experience: "2-4 years",
      description: "Join our design team to create beautiful, intuitive user interfaces that make technology accessible to everyone. You'll work on exciting projects from web applications to mobile apps.",
      requirements: [
        "2-4 years of UI design experience",
        "Proficiency in Figma, Sketch, or Adobe XD",
        "Strong portfolio demonstrating design thinking",
        "Understanding of responsive design principles",
        "Knowledge of design systems and component libraries",
        "Excellent communication and collaboration skills"
      ]
    },
    {
      title: "Web Developer",
      slug: "web-developer",
      department: "Engineering",
      location: "Remote / Perth",
      type: "Full-time",
      experience: "3-5 years",
      description: "Build scalable web applications using modern technologies. You'll be part of a team that values clean code, innovation, and creating solutions that make a real difference.",
      requirements: [
        "3-5 years of web development experience",
        "Strong proficiency in React, Node.js, and modern JavaScript",
        "Experience with databases (PostgreSQL, MongoDB)",
        "Knowledge of cloud platforms (AWS, Azure)",
        "Understanding of DevOps practices and CI/CD",
        "Passion for writing clean, maintainable code"
      ]
    }
  ];

  const benefits = [
    {
      icon: <Heart className="h-8 w-8" />,
      title: "Health & Wellness",
      description: "Comprehensive health insurance, mental health support, and wellness programs."
    },
    {
      icon: <Clock className="h-8 w-8" />,
      title: "Work-Life Balance",
      description: "Flexible working hours, remote work options, and generous paid time off."
    },
    {
      icon: <Lightbulb className="h-8 w-8" />,
      title: "Professional Growth",
      description: "Learning opportunities, conference attendance, and skill development programs."
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Collaborative Culture",
      description: "Work with passionate, diverse team members in an inclusive environment."
    },
    {
      icon: <Target className="h-8 w-8" />,
      title: "Meaningful Impact",
      description: "Contribute to products and initiatives that make a positive difference in the world."
    },
    {
      icon: <MapPin className="h-8 w-8" />,
      title: "Modern Office",
      description: "State-of-the-art office space in Perth with all the amenities you need."
    }
  ];

  const companyValues = [
    {
      title: "Innovation",
      description: "We encourage creative thinking and embrace new ideas that push boundaries."
    },
    {
      title: "Integrity",
      description: "We build trust through honest, transparent, and ethical practices."
    },
    {
      title: "Collaboration",
      description: "We believe the best solutions come from working together as a team."
    },
    {
      title: "Impact",
      description: "We measure our success by the positive difference we make in people's lives."
    },
    {
      title: "Compassion",
      description: "We care about our team, our clients, and the communities we serve."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-white via-blue-50/30 to-yellow-50/20 py-20 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-vincotek-dark leading-tight mb-6">
              Join Our{' '}
              <span className="bg-gradient-to-r from-vincotek-yellow to-vincotek-blue bg-clip-text text-transparent">
                Team
              </span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-12">
              Ready to be part of something meaningful? Join passionate professionals at Vincotek who are 
              building the future of technology while making a positive impact on the world.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#open-roles">
                <Button className="bg-vincotek-blue hover:bg-blue-600 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                  View Open Roles
                </Button>
              </a>
              <a href="#application">
                <Button variant="outline" className="border-vincotek-yellow text-vincotek-dark hover:bg-vincotek-yellow px-8 py-3 rounded-full transition-all duration-300">
                  Apply Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Vincotek */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Why Join Vincotek?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Be part of a company that values innovation, integrity, and making a positive impact in the world.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-semibold text-vincotek-dark mb-4">
                  Our Mission Drives Us
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  At Vincotek, we believe technology should empower everyone. We're not just building software – 
                  we're creating solutions that make a real difference in people's lives and work. 
                  When you join our team, you become part of this meaningful mission.
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-semibold text-vincotek-dark mb-4">
                  Values-Driven Culture
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Our five core values – Innovation, Integrity, Collaboration, Impact, and Compassion – 
                  aren't just words on a wall. They guide our daily decisions, shape our interactions, 
                  and define how we work together to achieve our goals.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d" 
                alt="Team collaboration" 
                className="w-full h-80 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-6 shadow-xl border border-gray-100">
                <div className="text-vincotek-dark text-lg font-bold">2.5%</div>
                <div className="text-gray-600 text-sm">Revenue for Good</div>
              </div>
            </div>
          </div>

          {/* Company Values */}
          <div>
            <h3 className="text-2xl font-semibold text-vincotek-dark mb-8 text-center">
              Our Values in Action
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {companyValues.map((value, index) => (
                <Card key={index} className="text-center p-6 border-0 bg-gradient-to-br from-gray-50 to-blue-50/30 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-white font-bold text-lg">{value.title.charAt(0)}</span>
                    </div>
                    <h4 className="text-lg font-semibold text-vincotek-dark mb-2">
                      {value.title}
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Benefits & Perks
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We believe in taking care of our team members so they can do their best work and thrive both professionally and personally.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white border-0">
                <CardContent className="p-8 text-center">
                  <div className="text-vincotek-blue mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-vincotek-dark mb-4">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Open Roles */}
      <section id="open-roles" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Open Positions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find your perfect role and join our mission to empower technology and enable growth.
            </p>
          </div>
          
          <div className="space-y-8">
            {openRoles.map((role, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white border-0">
                <CardContent className="p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-6">
                      <div>
                        <div className="flex flex-wrap items-center gap-4 mb-4">
                          <h3 className="text-2xl font-bold text-vincotek-dark">
                            {role.title}
                          </h3>
                          <span className="bg-vincotek-blue text-white px-3 py-1 rounded-full text-sm font-medium">
                            {role.department}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-6 text-sm text-gray-600 mb-4">
                          <div className="flex items-center">
                            <MapPin className="h-4 w-4 mr-1" />
                            {role.location}
                          </div>
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            {role.type}
                          </div>
                          <div className="flex items-center">
                            <Users className="h-4 w-4 mr-1" />
                            {role.experience}
                          </div>
                        </div>
                        <p className="text-gray-600 leading-relaxed">
                          {role.description}
                        </p>
                      </div>
                      
                      <div>
                        <h4 className="text-lg font-semibold text-vincotek-dark mb-3">
                          Key Requirements:
                        </h4>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {role.requirements.map((req, reqIndex) => (
                            <li key={reqIndex} className="flex items-start">
                              <div className="w-2 h-2 bg-vincotek-blue rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              <span className="text-sm text-gray-600">{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    <div className="flex flex-col justify-center">
                      <a href="#application">
                        <Button className="w-full bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark mb-4 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                          Apply for this Role
                        </Button>
                      </a>
                      <Link to={`/careers/${role.slug}`}>
                        <Button variant="outline" className="w-full border-vincotek-blue text-vincotek-blue hover:bg-vincotek-blue hover:text-white py-3 rounded-full transition-all duration-300">
                          Learn More
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="application" className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Join Us?
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Submit your application below and let's start a conversation about how you can 
                contribute to our mission and grow your career with Vincotek.
              </p>
            </div>
            
            <Card className="bg-white/10 backdrop-blur-sm border border-white/20">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="position" className="text-white">Position Applied</Label>
                      <Select onValueChange={handleSelectChange} required>
                        <SelectTrigger className="bg-white/10 border-white/20 text-white">
                          <SelectValue placeholder="Select a position" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ui-designer">UI Designer</SelectItem>
                          <SelectItem value="web-developer">Web Developer</SelectItem>
                          <SelectItem value="other">Other Position</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="fullName" className="text-white">Full Name</Label>
                      <Input
                        id="fullName"
                        name="fullName"
                        type="text"
                        value={applicationData.fullName}
                        onChange={handleInputChange}
                        required
                        className="bg-white/10 border-white/20 text-white placeholder-gray-300"
                        placeholder="Your full name"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="location" className="text-white">Location</Label>
                      <Input
                        id="location"
                        name="location"
                        type="text"
                        value={applicationData.location}
                        onChange={handleInputChange}
                        required
                        className="bg-white/10 border-white/20 text-white placeholder-gray-300"
                        placeholder="City, Country"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-white">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={applicationData.phone}
                        onChange={handleInputChange}
                        required
                        className="bg-white/10 border-white/20 text-white placeholder-gray-300"
                        placeholder="+61 xxx xxx xxx"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white">Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={applicationData.email}
                      onChange={handleInputChange}
                      required
                      className="bg-white/10 border-white/20 text-white placeholder-gray-300"
                      placeholder="your.email@example.com"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white">Short Message (Self-description)</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={applicationData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="bg-white/10 border-white/20 text-white placeholder-gray-300"
                      placeholder="Tell us about yourself, your experience, and why you'd like to join Vincotek..."
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark h-12 rounded-full font-semibold transition-all duration-300 hover:shadow-lg group"
                  >
                    Submit Application
                    <Send className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;